import React, { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router';
import { Box, CircularProgress, Typography, Button } from '@mui/material';

import { authEnabled } from './config';
import { useAppLogout } from './useAppLogout';
import { colors, spacing, typo } from '../theme/tokens';

interface Props {
  children: React.ReactNode;
}

/**
 * Displays a centered, accessible status screen — used for authenticating /
 * redirecting messages. Kept inline here because the error screen below has
 * additional actions; extract if a third caller appears.
 */
const StatusScreen: React.FC<{ message: string }> = ({ message }) => (
  <Box
    role="status"
    aria-live="polite"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      gap: spacing.md,
    }}
  >
    <CircularProgress sx={{ color: colors.brand }} aria-label={message} />
    <Typography sx={{ ...typo.secondaryText, color: colors.textSecondary }}>{message}</Typography>
  </Box>
);

const ProtectedRouteInner: React.FC<Props> = ({ children }) => {
  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0();
  const location = useLocation();
  const logout = useAppLogout();
  const redirectFired = useRef(false);

  useEffect(() => {
    if (isLoading || isAuthenticated || error) return;
    if (redirectFired.current) return;

    // Skip if we're mid-callback — Auth0's SDK is about to consume the
    // `code` + `state` params and firing our own redirect would clobber it
    // and produce an "Invalid state" error on reload.
    const qs = new URLSearchParams(window.location.search);
    if (qs.has('code') && qs.has('state')) return;

    // If axiosClient just redirected us to `/` after a 401, the original
    // URL is stashed in sessionStorage. Prefer that over the current
    // location (which is `/`) so the user returns to their deep link.
    let stashedReturnTo: string | null = null;
    try {
      stashedReturnTo = window.sessionStorage.getItem('postLoginReturnTo');
      if (stashedReturnTo) window.sessionStorage.removeItem('postLoginReturnTo');
    } catch {
      /* ignore storage failure */
    }
    const returnTo = stashedReturnTo || location.pathname + location.search + location.hash;

    redirectFired.current = true;
    loginWithRedirect({ appState: { returnTo } });
  }, [isLoading, isAuthenticated, error, loginWithRedirect, location]);

  if (isLoading) {
    return <StatusScreen message="Authenticating..." />;
  }

  if (error) {
    return (
      <Box
        role="alert"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: spacing.md,
          p: spacing.xl,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ ...typo.sectionTitle, color: colors.errorIcon }}>
          Authentication Error
        </Typography>
        <Typography sx={{ ...typo.secondaryText, color: colors.textSecondary, maxWidth: 480 }}>
          {error.message}
        </Typography>
        <Box sx={{ display: 'flex', gap: spacing.md, mt: spacing.md, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              redirectFired.current = false;
              loginWithRedirect({
                appState: {
                  returnTo: location.pathname + location.search + location.hash,
                },
              });
            }}
            sx={{
              textTransform: 'none',
              backgroundColor: colors.brand,
              color: colors.textPrimary,
              fontWeight: 600,
              '&:hover': { backgroundColor: colors.brandHover },
            }}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            onClick={logout}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Sign Out
          </Button>
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <StatusScreen message="Redirecting to login..." />;
  }

  return <>{children}</>;
};

const ProtectedRoute: React.FC<Props> = ({ children }) => {
  if (!authEnabled) return <>{children}</>;
  return <ProtectedRouteInner>{children}</ProtectedRouteInner>;
};

export default ProtectedRoute;
